import { ArrowLeft, Building2, Calendar, Users } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import multiSiteOwnersData from "../../mock/multiSiteOwners.json";
import { IMultiSiteOwner } from "../../interfaces/multiSiteOwner";
import KeyContactsTable from "../../components/multiSite/KeyContactsTable";
import RestaurantTable from "../../components/multiSite/RestaurantTable";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";

export default function MultiSiteOwnerDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const owner = (multiSiteOwnersData as IMultiSiteOwner[]).find((o) => o.id === id);

    if (!owner) {
        return (
            <div className="min-h-screen bg-canvas font-sans flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="h-12 w-12 text-ink-faint mx-auto mb-3" />
                    <p className="text-ink-muted">Propriétaire non trouvé</p>
                    <Button onClick={() => navigate("/multi-site")} className="mt-4">
                        Retour à la liste
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-8">
                {/* Back Button */}
                <button
                    onClick={() => navigate("/multi-site")}
                    className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink mb-6 group transition-colors duration-150"
                >
                    <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                    <span>Retour à la liste</span>
                </button>

                {/* Header */}
                <div className="bg-surface border border-border rounded-2xl p-6 mb-6">
                    <div className="flex items-start gap-6">
                        <div className="w-16 h-16 rounded-2xl bg-ink flex items-center justify-center flex-shrink-0">
                            <Building2 className="h-8 w-8 text-ink-inverse" />
                        </div>
                        <div className="flex-1">
                            <h1 className="font-sans text-2xl font-semibold tracking-tight text-ink mb-2">{owner.ownerName}</h1>
                            <p className="text-ink-muted mb-4">{owner.description}</p>
                            <div className="flex flex-wrap gap-2">
                                {owner.restaurantTypes.map((type, idx) => (
                                    <Badge key={idx} tone="neutral">
                                        {type}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-surface border border-border rounded-2xl p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-ink-muted" />
                            </div>
                            <div>
                                <p className="text-2xl font-semibold tracking-tight text-ink">{owner.numberOfRestaurants}</p>
                                <p className="text-xs text-ink-muted">Restaurants</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-surface border border-border rounded-2xl p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center">
                                <Users className="h-5 w-5 text-ink-muted" />
                            </div>
                            <div>
                                <p className="text-2xl font-semibold tracking-tight text-ink">{owner.totalEmployees}</p>
                                <p className="text-xs text-ink-muted">Employés</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-surface border border-border rounded-2xl p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center">
                                <Calendar className="h-5 w-5 text-ink-muted" />
                            </div>
                            <div>
                                <p className="text-2xl font-semibold tracking-tight text-ink">{owner.yearEstablished}</p>
                                <p className="text-xs text-ink-muted">Création</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-surface border border-border rounded-2xl p-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-surface-sunken rounded-lg flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-ink-muted" />
                            </div>
                            <div>
                                <p className="text-2xl font-semibold tracking-tight text-ink">{owner.cities.length}</p>
                                <p className="text-xs text-ink-muted">Villes</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Key Contacts */}
                <div className="bg-surface border border-border rounded-2xl p-6 mb-6">
                    <h2 className="text-base font-semibold text-ink mb-4">Contacts Clés</h2>
                    <KeyContactsTable contacts={owner.keyContacts} />
                </div>

                {/* Restaurants */}
                <div className="bg-surface border border-border rounded-2xl">
                    <div className="px-6 py-4 border-b border-border">
                        <h2 className="text-base font-semibold text-ink">
                            Restaurants ({owner.restaurants.length})
                        </h2>
                    </div>
                    <RestaurantTable restaurants={owner.restaurants} />
                </div>
            </div>
        </div>
    );
}
